import { ReactComponent as MandatoryAsterisk } from "assets/images/mandatoryAsterisk.svg"
import { IconCertified, ProgressBar } from "materia"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { FiestaLink } from "utility-components"
import { Stars } from "../../../components/Stars"
import { getProductLink } from "../../../dataUtilities/productDataUtilities"
import type { Data } from "./gql"
import s from "./JourneyCard.module.scss"

export const JourneyCard = ({
  data,
  isLocked,
}: {
  data: Data
  isLocked: boolean
}) => {
  const { title, id, productType, progress, course } = data

  const isFiveStarProduct = progress.starsMax === 5
  const isActivity = productType === "activity"

  return (
    <FiestaLink
      to={isLocked ? undefined : getProductLink(id, productType)}
      className={modifiers(s, "card", { isLocked, isActivity })}
      aria-haspopup="dialog"
    >
      <div className={s.bottom}>
        <div className={s.rowOne}>
          <h3 className={modifiers(s, "title", { isActivity })}>{title}</h3>
        </div>
        <div className={s.rowTwo}>
          <div className={s.rowTwo__wrapper}>
            <StarsWrapper data={data} />
            {course?.isAttending ? (
              <Attending />
            ) : !isFiveStarProduct ? (
              <ProductType data={data} />
            ) : null}
          </div>
        </div>
        {isActivity && <ActivityProgress data={data} />}
      </div>
      <CornerIcon progress={progress} />
    </FiestaLink>
  )
}

const StarsWrapper = ({ data }: { data: Data }) => {
  const { progress } = data
  const { starsMax, starsCount } = progress

  if (starsMax === 0) return

  return (
    <div className={s.stars}>
      <Stars
        count={starsCount}
        max={starsMax}
        size="small"
        emptyStarColor="concrete"
      />
    </div>
  )
}

const ProductType = ({ data }: { data: Data }) => {
  const { t } = useTranslation()

  const { productType, progress, hasPlayedXapi } = data
  const inProgress = hasPlayedXapi && !progress.certified
  const productTypeText = t(`product.PRODUCT_TYPES.${productType}`)

  return (
    <div className={s.productType}>
      {productTypeText} {inProgress && ` (${t("product.PRODUCT_IN_PROGRESS")})`}
    </div>
  )
}

const Attending = () => {
  const { t } = useTranslation()
  return <div className={s.attending}>{t("event.ATTENDING")}</div>
}

const ActivityProgress = ({ data }: { data: Data }) => {
  const { activityProgress, progress, productType } = data
  const isActivity = productType === "activity"

  return (
    <div className={modifiers(s, "progress", { isActivity })}>
      <ProgressBar
        value={activityProgress?.progressCount ?? 0}
        maxValue={activityProgress?.progressOf ?? 100}
        color={progress.certified ? "green-30" : "blue-40"}
      />
    </div>
  )
}

const CornerIcon = ({ progress }: { progress: Data["progress"] }) => {
  const { t } = useTranslation()

  if (progress.certified) {
    return (
      <IconCertified title="Completed" className={s.cornerIcon} size="large" />
    )
  }
  if (progress.mandatory) {
    return (
      <MandatoryAsterisk
        className={s.cornerIcon}
        role="img"
        aria-label={t("user.MANDATORY")}
      />
    )
  }
  return null
}
